import {
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  type SyntheticEvent,
} from 'react';

import { Icon, type IconName } from './Icon';
import { useFieldWiring } from './useFieldWiring';
import {
  REMOTE_SEARCH_DELAY_MS,
  REMOTE_SEARCH_MIN_LENGTH,
  useRemoteSearch,
} from './useRemoteSearch';

/** One row the person can choose. */
export interface SearchComboboxOption<T = unknown> {
  /** Unique within the whole list, also what the row's element id is built from. */
  readonly key: string;
  /** What the row is called. It is part of the row's accessible name, so every word is real. */
  readonly label: string;
  /** A second line: an address, a number, a status in words. */
  readonly description?: string;
  readonly icon?: IconName;
  /** Something drawn after the words, such as a status chip. Its text joins the row's name. */
  readonly trailing?: ReactNode;
  /** A `command` runs something, such as "See all jobs"; a `record` opens one. */
  readonly kind?: 'record' | 'command';
  /** Whatever the caller needs to act on the choice. */
  readonly data: T;
}

/** Rows that belong together, under an optional heading. */
export interface SearchComboboxGroup<T = unknown> {
  readonly key: string;
  readonly heading?: string;
  readonly options: readonly SearchComboboxOption<T>[];
}

/** What to say, and offer, when nothing matched. */
export interface SearchComboboxEmpty<T = unknown> {
  readonly message: string;
  /** Rows to offer beside the message, such as creating what was looked for. */
  readonly actions?: readonly SearchComboboxOption<T>[];
}

export interface SearchComboboxProps<T = unknown> {
  /** Accessible name, required when the field is used outside a FormField. */
  readonly 'aria-label'?: string;
  /** What the list of rows is called, when that is not the field's own name: "Customers". */
  readonly listLabel?: string;
  readonly placeholder?: string;
  /**
   * Looks for rows on the server once typing pauses. A newer search cancels the older one through
   * the signal it is given, so pass it on to the request.
   */
  readonly search?: (
    term: string,
    signal: AbortSignal,
  ) => Promise<readonly SearchComboboxGroup<T>[]>;
  /** What the search is over; a change throws away what was found and searches again. */
  readonly searchScope?: string;
  /** Rows that need no wait, such as pages. Asked on every keystroke, listed above the server's. */
  readonly instant?: (term: string) => readonly SearchComboboxGroup<T>[];
  /** What to list before anything is typed, such as recent records. */
  readonly idle?: readonly SearchComboboxGroup<T>[];
  /** Said while the list is open and there is nothing else to say, such as how much to type. */
  readonly hint?: string;
  /** What to say, and offer, when nothing matched. The default is a plain sentence. */
  readonly empty?: (term: string) => SearchComboboxEmpty<T>;
  /** Called with the chosen row and the words that were typed. */
  readonly onSelect: (option: SearchComboboxOption<T>, term: string) => void;
  /** Text shown while the list is closed, for a field that holds a choice. */
  readonly selectedLabel?: string;
  /** A glyph at the start of the field. */
  readonly icon?: IconName;
  /** Something at the end of the field, such as the shortcut that focuses it. */
  readonly trailing?: ReactNode;
  readonly minLength?: number;
  readonly delayMs?: number;
  readonly maxLength?: number;
  readonly inputRef?: Ref<HTMLInputElement>;
}

const RETRY_KEY = '__retry';

const NO_GROUPS: readonly never[] = [];
const NO_SEARCH = (): Promise<readonly never[]> => Promise.resolve(NO_GROUPS);

function countOf<T>(groups: readonly SearchComboboxGroup<T>[]): number {
  return groups.reduce((sum, group) => sum + group.options.length, 0);
}

/** Said when the server could not be reached; the row that follows it asks again. */
const SEARCH_FAILED = 'Couldn’t search. Try again.';

function resultCount(count: number): string {
  return `${String(count)} ${count === 1 ? 'result' : 'results'}`;
}

/**
 * A field that finds things on a server as you type, and lets you choose one.
 *
 * It is the combobox the customer and pricebook finders each wrote for themselves, made once: the
 * words are waited on until typing pauses, the search runs once for them, and one that is
 * overtaken is cancelled. The results are listed under their headings, the arrow keys move
 * through every row of every heading, and Enter takes the highlighted one, so the top hit is one
 * keystroke away. When Enter comes before the answer does, it waits for the answer and then takes
 * the top hit, rather than opening a row that belongs to words since changed.
 *
 * Rows that need no wait (pages, say) arrive through `instant` and are listed first. `idle` lists
 * something before anything is typed. When nothing matched, the field says so and can offer a way
 * on, as rows that are reached the same way as any other. The count is announced politely once
 * the answer is in, never keystroke by keystroke.
 *
 * Focus never leaves the text field (`aria-activedescendant`, the ARIA 1.2 combobox pattern), so
 * typing keeps working while a row is highlighted, and a click on a row cannot blur the field
 * before it lands.
 *
 * @param props see {@link SearchComboboxProps}
 * @returns the field and its list
 */
export function SearchCombobox<T>({
  'aria-label': ariaLabel,
  listLabel,
  placeholder,
  search,
  searchScope,
  instant,
  idle,
  hint,
  empty,
  onSelect,
  selectedLabel,
  icon,
  trailing,
  minLength = REMOTE_SEARCH_MIN_LENGTH,
  delayMs = REMOTE_SEARCH_DELAY_MS,
  maxLength,
  inputRef,
}: SearchComboboxProps<T>): React.JSX.Element {
  const { controlId, describedBy, invalid, required } = useFieldWiring();
  const listId = `${controlId}-listbox`;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const waitingForEnter = useRef(false);
  const scrollToActive = useRef(false);

  const term = query.trim();
  const short = term.length < minLength;
  const remote = useRemoteSearch<readonly SearchComboboxGroup<T>[]>(term, search ?? NO_SEARCH, {
    delayMs,
    minLength,
    enabled: search !== undefined,
    ...(searchScope === undefined ? {} : { scope: searchScope }),
  });

  const quick = term === '' ? NO_GROUPS : (instant?.(term) ?? NO_GROUPS);
  const found = remote.data ?? NO_GROUPS;
  const resting = term === '' ? (idle ?? NO_GROUPS) : NO_GROUPS;
  const matched = countOf(quick) + countOf(found);
  const answered = search === undefined || remote.status === 'ready';
  const nothingFound = term !== '' && !short && matched === 0 && answered;
  const emptyContent: SearchComboboxEmpty<T> | null = nothingFound
    ? (empty?.(term) ?? { message: `No results for “${term}”` })
    : null;

  const groups: SearchComboboxGroup<T>[] = [...resting, ...quick, ...found].filter(
    (group) => group.options.length > 0,
  );
  if (emptyContent?.actions !== undefined && emptyContent.actions.length > 0) {
    groups.push({ key: '__empty', options: emptyContent.actions });
  }
  if (remote.status === 'error') {
    groups.push({
      key: RETRY_KEY,
      options: [{ key: RETRY_KEY, label: 'Try again', kind: 'command', data: undefined as never }],
    });
  }
  const options = groups.flatMap((group) => group.options);
  const instantCount = countOf(resting) + countOf(quick);
  const active = Math.min(activeIndex, Math.max(0, options.length - 1));
  const showList = open && options.length > 0;

  let note: string | null = null;
  if (open) {
    if (remote.status === 'error') {
      note = SEARCH_FAILED;
    } else if (emptyContent !== null) {
      note = emptyContent.message;
    } else if (remote.status === 'searching') {
      note = 'Searching…';
    } else if (term !== '' && short && matched === 0) {
      note = hint ?? `Type at least ${String(minLength)} characters.`;
    } else if (term === '' && options.length === 0 && hint !== undefined) {
      note = hint;
    }
  }

  let announcement = '';
  if (open && remote.status === 'error') {
    announcement = SEARCH_FAILED;
  } else if (open && term !== '' && remote.status !== 'searching') {
    announcement = emptyContent?.message ?? (matched > 0 ? resultCount(matched) : '');
  }

  const activeOptionId = showList ? `${listId}-option-${String(active)}` : undefined;
  // Only a key moves the list under the pointer: scrolling on hover would chase the mouse.
  useEffect(() => {
    if (activeOptionId !== undefined && scrollToActive.current) {
      document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' });
    }
    scrollToActive.current = false;
  }, [activeOptionId]);

  function close(): void {
    waitingForEnter.current = false;
    setOpen(false);
    setQuery('');
  }

  function choose(index: number): void {
    const option = options[index];
    if (option === undefined) {
      return;
    }
    if (option.key === RETRY_KEY) {
      remote.retry();
      return;
    }
    onSelect(option, term);
    close();
  }

  // Enter that came before the answer is honoured once the answer is in, and only if it found
  // something: the top hit of the words now typed, not of the words before.
  const settleWaitingEnter = useEffectEvent(() => {
    if (!waitingForEnter.current || remote.status === 'searching') {
      return;
    }
    waitingForEnter.current = false;
    if (remote.status === 'ready' && options.length > 0) {
      choose(active);
    }
  });
  useEffect(() => {
    // A turn later, because choosing a row closes and clears the field: state set in the effect's
    // own turn would draw the field twice for one answer.
    queueMicrotask(() => {
      settleWaitingEnter();
    });
  }, [remote.status]);

  function move(step: 1 | -1): void {
    scrollToActive.current = true;
    if (!open) {
      setOpen(true);
    } else if (options.length > 0) {
      setActiveIndex((active + step + options.length) % options.length);
    }
  }

  function pressEnter(event: KeyboardEvent<HTMLInputElement>): void {
    if (!open) {
      return;
    }
    event.preventDefault();
    const waitingOnOlderWords =
      remote.status === 'searching' && (options.length === 0 || active >= instantCount);
    if (waitingOnOlderWords) {
      waitingForEnter.current = true;
      return;
    }
    choose(active);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        move(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        move(-1);
        return;
      case 'Enter':
        pressEnter(event);
        return;
      case 'Escape':
        // Closes the list and clears the words, and stops there: Escape inside a list that is
        // showing must not also close the dialog the field sits in.
        if (showList || note !== null || query !== '') {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
        return;
      default:
    }
  }

  // So typing replaces a choice already shown instead of adding to its words. A click is handled
  // as well as focus, because the browser places the caret after focus on a mouse click.
  function selectShownText(event: SyntheticEvent<HTMLInputElement>): void {
    if (!open && selectedLabel !== undefined) {
      event.currentTarget.select();
    }
  }

  const keepFocus = (event: SyntheticEvent): void => {
    // A click on a row would blur the field first, and the blur closes the list before the click
    // lands. Safari does not focus a button on click, so it needs this as much as the rest.
    event.preventDefault();
  };

  return (
    <div className="fo-search" data-invalid={invalid ? '' : undefined}>
      <div className="fo-search__control">
        {icon === undefined ? null : <Icon name={icon} className="fo-search__icon" />}
        <input
          ref={inputRef}
          id={controlId}
          className="fo-search__input"
          type="text"
          role="combobox"
          autoComplete="off"
          spellCheck={false}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeOptionId}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-label={ariaLabel}
          required={required}
          placeholder={placeholder}
          maxLength={maxLength}
          value={open ? query : (selectedLabel ?? '')}
          onChange={(event) => {
            waitingForEnter.current = false;
            setQuery(event.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onFocus={(event) => {
            if (selectedLabel === undefined) {
              setOpen(true);
            }
            selectShownText(event);
          }}
          onClick={selectShownText}
          onKeyDown={handleKeyDown}
          onBlur={close}
        />
        {trailing === undefined ? null : <span className="fo-search__trailing">{trailing}</span>}
      </div>

      {/* The count is announced politely and only once the answer is in, so a screen-reader user
          hears one sentence per pause in typing, not one per key. */}
      <span className="visually-hidden" role="status">
        {announcement}
      </span>

      <div className="fo-search__popup" hidden={!showList && note === null}>
        <div
          id={listId}
          className="fo-search__list"
          role="listbox"
          aria-label={listLabel ?? ariaLabel ?? 'Suggestions'}
          aria-busy={remote.status === 'searching' || undefined}
          hidden={!showList}
        >
          {groups.map((group, groupIndex) => {
            const start = countOf(groups.slice(0, groupIndex));
            const headingId = `${listId}-heading-${group.key}`;
            return (
              <div
                key={group.key}
                role="group"
                className="fo-search__group"
                aria-labelledby={group.heading === undefined ? undefined : headingId}
              >
                {group.heading === undefined ? null : (
                  <div role="presentation" id={headingId} className="fo-search__heading">
                    {group.heading}
                  </div>
                )}
                {group.options.map((option, index) => {
                  const position = start + index;
                  return (
                    <div
                      key={option.key}
                      id={`${listId}-option-${String(position)}`}
                      className="fo-search__option"
                      role="option"
                      aria-selected={position === active}
                      data-active={position === active ? '' : undefined}
                      data-kind={option.kind ?? 'record'}
                      onMouseDown={keepFocus}
                      onClick={() => {
                        choose(position);
                      }}
                      onMouseEnter={() => {
                        scrollToActive.current = false;
                        setActiveIndex(position);
                      }}
                    >
                      {option.icon === undefined ? null : (
                        <Icon name={option.icon} className="fo-search__option-icon" />
                      )}
                      <span className="fo-search__option-text">
                        <span className="fo-search__option-label">{option.label}</span>
                        {option.description === undefined ? null : (
                          <>
                            {' '}
                            <span className="fo-search__option-description">
                              {option.description}
                            </span>
                          </>
                        )}
                      </span>
                      {option.trailing === undefined ? null : (
                        <>
                          {' '}
                          <span className="fo-search__option-trailing">{option.trailing}</span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        {note === null ? null : <p className="fo-search__note">{note}</p>}
      </div>
    </div>
  );
}
